import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Rocket, TrendingUp, Lightbulb, ChevronRight } from 'lucide-react';
export function OnboardingSlide() {
  const [activePhase, setActivePhase] = useState<number | null>(null);
  const phases = [{
    id: 1,
    title: 'Foundations Call',
    time: 'Week 1',
    icon: Target,
    desc: 'Access to LetLois and teach you the basics to get you immediate wins',
    outcomes: ['Custom roadmap built', 'Quick wins identified', 'Timeline established'],
    duration: '60 minutes'
  }, {
    id: 2,
    title: 'Website Launch',
    time: 'Weeks 2-4',
    icon: Rocket,
    desc: 'Your build will be complete; you can then review and adjust',
    outcomes: ['3-layer optimized website live', 'Conversion tracking installed', 'First leads coming in'],
    duration: '45 minutes'
  }, {
    id: 3,
    title: 'Next Steps Coaching',
    time: 'Week 4',
    icon: TrendingUp,
    desc: 'The team makes any last-minute changes to the process',
    outcomes: ['Performance review', 'Strategy refinement', 'Decision point (Grow/Boost/Delegate)'],
    duration: '60 minutes'
  }, {
    id: 4,
    title: 'Support & Coaching',
    time: 'Ongoing',
    icon: Lightbulb,
    desc: "We'll help you stay focused and your tech to just work",
    outcomes: ['Continuous optimization', 'Team alignment', 'Scaling strategy'],
    duration: '30 minutes / once a month'
  }];
  return <section id="process" className="min-h-screen bg-dark-section py-24 px-4 flex flex-col justify-center" data-id="element-316">
      <div className="max-w-7xl mx-auto w-full" data-id="element-317">
        <div className="text-center mb-16" data-id="element-318">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-319">
            Your Journey with LetLois
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-320">
            From signup to results in weeks, not months
          </motion.p>
        </div>

        {/* Timeline Container */}
        <div className="relative py-12 mb-24" data-id="element-321">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-[32px] left-0 w-full h-1 bg-white/10 rounded-full overflow-hidden z-0" data-id="element-322">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '100%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} className="h-full bg-primary" data-id="element-323" />
          </div>

          {/* Connecting Line (Mobile) */}
          <div className="md:hidden absolute top-0 left-8 w-1 h-full bg-white/10 -translate-x-1/2 rounded-full overflow-hidden z-0" data-id="element-324">
            <motion.div initial={{
            height: 0
          }} whileInView={{
            height: '100%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.5,
            ease: 'easeInOut'
          }} className="w-full bg-primary" data-id="element-325" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4 relative z-10" data-id="element-326">
            {phases.map((phase, index) => <motion.div key={phase.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="relative pl-20 md:pl-0 group" onMouseEnter={() => setActivePhase(phase.id)} onMouseLeave={() => setActivePhase(null)} data-id="element-327">
                {/* Node */}
                <div className={`absolute left-0 md:left-1/2 top-0 md:top-0 md:-translate-x-1/2 w-16 h-16 rounded-full border-4 border-dark bg-dark-card flex items-center justify-center transition-all duration-300 z-20 ${activePhase === phase.id ? 'scale-110 border-primary shadow-[0_0_20px_rgba(26,139,246,0.5)]' : 'border-white/10'}`} data-id="element-328">
                  <phase.icon size={24} className={`transition-colors duration-300 ${activePhase === phase.id ? 'text-primary' : 'text-text-muted'}`} data-id="element-329" />
                </div>

                {/* Time label below node on desktop */}
                <div className="hidden md:block text-center mt-20 mb-3" data-id="element-330">
                  <span className="text-xs font-bold text-text-dim uppercase tracking-wider" data-id="element-331">
                    {phase.time}
                  </span>
                </div>

                {/* Time label above card on mobile */}
                <div className="md:hidden absolute -top-6 left-20 text-xs font-bold text-text-dim uppercase tracking-wider" data-id="element-332">
                  {phase.time}
                </div>

                {/* Content Card */}
                <div className={`md:mt-2 bg-dark-card border ${activePhase === phase.id ? 'border-primary/50' : 'border-white/10'} p-6 rounded-xl transition-all duration-300 hover:bg-white/5 relative`} data-id="element-333">
                  <span className="text-4xl font-bold text-white/5 absolute right-4 top-4 select-none" data-id="element-334">
                    0{phase.id}
                  </span>
                  <h3 className={`text-lg font-bold transition-colors mb-2 ${activePhase === phase.id ? 'text-primary' : 'text-white'}`} data-id="element-335">
                    {phase.title}
                  </h3>

                  <p className="text-sm text-text-muted mb-4 leading-relaxed" data-id="element-336">
                    {phase.desc}
                  </p>

                  <AnimatePresence data-id="element-337">
                    {activePhase === phase.id && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-338">
                        <ul className="space-y-2 mb-4" data-id="element-339">
                          {phase.outcomes.map((outcome, i) => <li key={i} className="flex items-start text-xs text-text-dim" data-id="element-340">
                              <ChevronRight size={12} className="mr-1 mt-0.5 text-primary shrink-0" data-id="element-341" />
                              {outcome}
                            </li>)}
                        </ul>
                        <div className="text-xs font-bold text-primary bg-primary/10 py-1 px-2 rounded inline-block" data-id="element-342">
                          Duration: {phase.duration}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Mobile Expand Hint */}
                  <div className="md:hidden mt-2 text-xs text-primary flex items-center justify-end" data-id="element-343">
                    {activePhase !== phase.id && 'Tap for details'}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 1
      }} className="text-center mb-24" data-id="element-344">
          <p className="text-lg font-medium text-white bg-gradient-to-r from-transparent via-primary/20 to-transparent py-2" data-id="element-345">
            Most clients see results by{' '}
            <span className="text-primary font-bold" data-id="element-346">Week 2</span>
          </p>
        </motion.div>

        {/* Team Section */}
        <div className="mb-24" data-id="element-347">
          <h3 className="text-3xl font-bold text-center mb-12" data-id="element-348">Led By</h3>
          <div className="bg-dark-card border border-white/10 rounded-3xl overflow-hidden max-w-5xl mx-auto flex flex-col md:flex-row" data-id="element-349">
            <div className="md:w-1/2 relative min-h-[400px]" data-id="element-350">
              <img src="https://cdn.magicpatterns.com/uploads/qz61qp5bRViogfyQgchbuR/LetLois_People_(1).png" alt="Josh and Noah" className="absolute inset-0 w-full h-full object-cover" data-id="element-351" />
            </div>
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center space-y-8" data-id="element-352">
              <div data-id="element-353">
                <h4 className="text-2xl font-bold text-white mb-2" data-id="element-354">Noah</h4>
                <p className="text-text-muted text-lg leading-relaxed" data-id="element-355">
                  The #1 appointment setter in the world ranked by data giant
                  ZoomInfo. Generating more appointments in one year than most
                  business owners do in a lifetime.
                </p>
              </div>
              <div className="w-full h-px bg-white/10" data-id="element-356" />
              <div data-id="element-357">
                <h4 className="text-2xl font-bold text-white mb-2" data-id="element-358">Josh</h4>
                <p className="text-text-muted text-lg leading-relaxed" data-id="element-359">
                  A nerd who's scaled over 500 local businesses, with top
                  clients making the Inc 5000 multiple times.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Case Study Section */}
        <div data-id="element-360">
          <div className="text-center mb-12" data-id="element-361">
            <h3 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-362">
              280% Growth. Less Than 4% Churn.
            </h3>
            <p className="text-xl text-text-muted" data-id="element-363">
              Helping a global appointment coaching company scale in just two
              years.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-364">
            <div className="bg-white rounded-2xl p-4 shadow-xl overflow-hidden" data-id="element-365">
              <img src="https://cdn.magicpatterns.com/uploads/8tMHMoaQgaWoJCfQnZMCPY/Case_Studies_16.png" alt="Stripe MRR Growth" className="w-full h-auto rounded-lg" data-id="element-366" />
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-xl overflow-hidden" data-id="element-367">
              <img src="https://cdn.magicpatterns.com/uploads/oh5K1ubg2cYiWmiQx83YDb/Case_Studies_14.png" alt="Stripe Volume Growth" className="w-full h-auto rounded-lg" data-id="element-368" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}